import React from 'react';
import { Box, Paper, Typography, LinearProgress, Skeleton, Tooltip as MuiTooltip } from '@mui/material';

// Shared visual language for the dashboard and WhatsApp analytics pages.
// ---------------------------------------------------------------------------
// Visual vocabulary: every category / pricing type keeps one colour across
// the composition bar, the legend, the chart and the table.
// ---------------------------------------------------------------------------
export const INK = '#1F2A3D';
export const MUTED = '#5B6577';
export const RULE = '#E3E7EE';

export const CATEGORY_META = {
    MARKETING: { label: 'Marketing', color: '#6D5BD0' },
    MARKETING_LITE: { label: 'Marketing (lite)', color: '#A99BEA' },
    UTILITY: { label: 'Utility', color: '#0E7C86' },
    AUTHENTICATION: { label: 'Authentication', color: '#C77700' },
    AUTHENTICATION_INTERNATIONAL: { label: 'Authentication (international)', color: '#E3A447' },
    SERVICE: { label: 'Service', color: '#1BAA52' },
    REFERRAL_CONVERSION: { label: 'Referral conversion', color: '#C2457A' },
    UNKNOWN: { label: 'Other', color: '#8A94A6' }
};

export const PRICING_TYPE_META = {
    REGULAR: { label: 'Paid', color: INK },
    FREE_CUSTOMER_SERVICE: { label: 'Free, customer service window', color: '#1BAA52' },
    FREE_ENTRY_POINT: { label: 'Free, from an ad or entry point', color: '#7FD8A4' },
    UNKNOWN: { label: 'Other', color: '#8A94A6' }
};

export const categoryMeta = (key) => CATEGORY_META[key] || { label: key, color: CATEGORY_META.UNKNOWN.color };
export const pricingTypeMeta = (key) => PRICING_TYPE_META[key] || { label: key, color: PRICING_TYPE_META.UNKNOWN.color };

export const prefersReducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export const formatCount = (value) => new Intl.NumberFormat('en-IN').format(Math.round(value || 0));
export const formatINR = (value) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(value || 0);
export const formatPercent = (value) => `${(value || 0).toFixed(1)}%`;

// Sum a numeric field of data points grouped by a key
export const groupTotals = (points, keyField, fields) => {
    const groups = {};
    points.forEach((point) => {
        const key = point[keyField] || 'UNKNOWN';
        if (!groups[key]) {
            groups[key] = { key };
            fields.forEach((f) => { groups[key][f] = 0; });
        }
        fields.forEach((f) => { groups[key][f] += point[f] || 0; });
    });
    return Object.values(groups);
};

// ---------------------------------------------------------------------------
// Presentational pieces
// ---------------------------------------------------------------------------
export const Section = ({ title, description, action, children, sx }) => (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderColor: RULE, borderRadius: 2, ...sx }}>
        {(title || action) && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5 }}>
                <Box>
                    {title && (
                        <Typography component="h2" sx={{ fontSize: '1.05rem', fontWeight: 600, color: INK }}>
                            {title}
                        </Typography>
                    )}
                    {description && (
                        <Typography variant="body2" sx={{ color: MUTED, mt: 0.5, maxWidth: '65ch' }}>
                            {description}
                        </Typography>
                    )}
                </Box>
                {action}
            </Box>
        )}
        {children}
    </Paper>
);

// A strip of headline figures separated by rules (not a grid of cards)
export const FigureStrip = ({ figures }) => (
    <Paper
        variant="outlined"
        sx={{
            mb: 3,
            borderColor: RULE,
            borderRadius: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: `repeat(${figures.length}, 1fr)` },
            overflow: 'hidden'
        }}
    >
        {figures.map((figure, index) => (
            <Box
                key={figure.label}
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                    borderLeft: { xs: 'none', sm: index === 0 ? 'none' : `1px solid ${RULE}` },
                    borderTop: { xs: index === 0 ? 'none' : `1px solid ${RULE}`, sm: 'none' }
                }}
            >
                <Typography variant="body2" sx={{ color: MUTED, mb: 0.75 }}>
                    {figure.label}
                </Typography>
                <Typography
                    sx={{
                        fontSize: { xs: '1.75rem', md: '2.125rem' },
                        fontWeight: 600,
                        lineHeight: 1.1,
                        letterSpacing: '-0.02em',
                        color: figure.unavailable ? MUTED : INK,
                        fontVariantNumeric: 'tabular-nums'
                    }}
                >
                    {figure.value}
                </Typography>
                {figure.meter !== undefined && (
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(100, Math.max(0, figure.meter))}
                        aria-label={`${figure.label} ${formatPercent(figure.meter)}`}
                        sx={{
                            mt: 1.25,
                            height: 6,
                            borderRadius: 3,
                            bgcolor: RULE,
                            '& .MuiLinearProgress-bar': { bgcolor: 'primary.main', borderRadius: 3 }
                        }}
                    />
                )}
                {figure.hint && (
                    <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 0.75 }}>
                        {figure.hint}
                    </Typography>
                )}
            </Box>
        ))}
    </Paper>
);

// One full-width bar split into segments, with a legend list underneath
export const CompositionBar = ({ items, valueLabel, showCost }) => {
    const total = items.reduce((sum, item) => sum + item.value, 0);
    if (!total) return null;
    const sorted = [...items].sort((a, b) => b.value - a.value);

    return (
        <Box>
            <Box
                role="img"
                aria-label={sorted.map((item) => `${item.label} ${formatPercent((item.value / total) * 100)}`).join(', ')}
                sx={{ display: 'flex', height: 20, borderRadius: 1, overflow: 'hidden', gap: '2px', mb: 2.5 }}
            >
                {sorted.map((item) => (
                    <MuiTooltip key={item.key} title={`${item.label}: ${formatCount(item.value)} ${valueLabel}`} arrow>
                        <Box sx={{ flexGrow: item.value, flexBasis: 0, minWidth: 3, bgcolor: item.color }} />
                    </MuiTooltip>
                ))}
            </Box>

            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {sorted.map((item) => (
                    <Box
                        component="li"
                        key={item.key}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: showCost ? '1fr auto auto auto' : '1fr auto auto',
                            columnGap: { xs: 2, sm: 4 },
                            alignItems: 'center',
                            py: 1.25,
                            borderTop: `1px solid ${RULE}`,
                            fontVariantNumeric: 'tabular-nums'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: item.color, flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ color: INK, fontWeight: 500 }} noWrap>
                                {item.label}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: INK, textAlign: 'right' }}>
                            {formatCount(item.value)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: MUTED, textAlign: 'right', minWidth: 52 }}>
                            {formatPercent((item.value / total) * 100)}
                        </Typography>
                        {showCost && (
                            <Typography variant="body2" sx={{ color: INK, textAlign: 'right', minWidth: 90 }}>
                                {formatINR(item.cost)}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export const ChartTooltip = ({ active, payload, label, formatter }) => {
    if (!active || !payload?.length) return null;
    return (
        <Paper sx={{ px: 1.5, py: 1, borderRadius: 1.5, boxShadow: '0 4px 16px rgba(31, 42, 61, 0.14)' }}>
            <Typography variant="caption" sx={{ color: MUTED, display: 'block', mb: 0.5 }}>{label}</Typography>
            {payload.filter((entry) => entry.value).map((entry) => (
                <Box key={entry.dataKey} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontVariantNumeric: 'tabular-nums' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: entry.color }} />
                    <Typography variant="body2" sx={{ color: INK, flexGrow: 1 }}>{entry.name}</Typography>
                    <Typography variant="body2" sx={{ color: INK, fontWeight: 600, ml: 2 }}>
                        {formatter ? formatter(entry.value) : formatCount(entry.value)}
                    </Typography>
                </Box>
            ))}
        </Paper>
    );
};

export const EmptyState = ({ title, children }) => (
    <Paper variant="outlined" sx={{ p: { xs: 3, sm: 5 }, borderColor: RULE, borderRadius: 2, textAlign: 'left' }}>
        <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>{title}</Typography>
        <Typography variant="body2" sx={{ color: MUTED, maxWidth: '60ch' }}>{children}</Typography>
    </Paper>
);

export const LoadingState = () => (
    <Box aria-busy="true">
        <Skeleton variant="rounded" height={112} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={320} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={200} />
    </Box>
);

export const axisProps = {
    tick: { fill: MUTED, fontSize: 12 },
    tickLine: false,
    axisLine: { stroke: RULE }
};
